import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, Check, Search } from 'lucide-react';
import { useExclusiveOpen } from '../hooks/useExclusiveOpen';

export interface DropdownOption {
  value: string;
  label: string;
}

interface CustomDropdownProps {
  value: string;
  onChange: (v: string) => void;
  options: DropdownOption[];
  placeholder?: string;
  disabled?: boolean;
  searchable?: boolean;
  className?: string;
  /** Optional: max-height of the option list (in px). Default 240. */
  maxHeight?: number;
  /**
   * If provided, only one dropdown with this id can be open at a time.
   * Opening one closes all others sharing the same registry.
   */
  openId?: string;
}

export const CustomDropdown: React.FC<CustomDropdownProps> = ({
  value,
  onChange,
  options,
  placeholder = '-- Chọn --',
  disabled = false,
  searchable = true,
  className = '',
  maxHeight = 240,
  openId,
}) => {
  const [localOpen, setLocalOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [flipUp, setFlipUp] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const [exclusiveOpen, setExclusiveOpen] = useExclusiveOpen(openId ?? '__none__');
  const open = openId ? exclusiveOpen : localOpen;

  const setOpen = openId ? setExclusiveOpen : setLocalOpen;

  const selected = options.find((o) => o.value === value);

  useEffect(() => {
    if (!open) {
      setSearch('');
      return;
    }
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open, setOpen]);

  useEffect(() => {
    if (open && searchable) {
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  }, [open, searchable]);

  // Flip up if dropdown would overflow viewport bottom.
  useEffect(() => {
    if (!open || !containerRef.current) return;
    const update = () => {
      if (!containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      // Flip up if space below < maxHeight + some margin AND space above > space below
      setFlipUp(spaceBelow < maxHeight + 16 && spaceAbove > spaceBelow);
    };
    update();
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [open, maxHeight]);

  const filtered = searchable && search
    ? options.filter((o) => o.label.toLowerCase().includes(search.toLowerCase()))
    : options;

  const handleSelect = (v: string) => {
    onChange(v);
    setOpen(false);
  };

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => !disabled && setOpen(!open)}
        className={`flex w-full items-center justify-between gap-2 rounded-lg border border-[#252a38] bg-[#0d0f14] px-3.5 py-2.5 pr-9 text-left text-[12px] font-semibold transition-colors ${
          disabled
            ? 'cursor-not-allowed opacity-50'
            : open
              ? 'border-[#3b82f6]'
              : 'hover:border-[#334155]'
        } ${selected ? 'text-[#e2e8f0]' : 'text-[#64748b]'}`}
      >
        <span className="truncate">{selected ? selected.label : placeholder}</span>
        <ChevronDown
          size={14}
          className={`shrink-0 text-[#64748b] transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>

      {open && (
        <div
          className={`absolute left-0 right-0 z-50 overflow-hidden rounded-lg border border-[#252a38] bg-[#161920] shadow-[0_10px_25px_-5px_rgba(0,0,0,0.5)] ${
            flipUp ? 'bottom-full mb-1' : 'top-full mt-1'
          }`}
          role="listbox"
        >
          {searchable && (
            <div className="border-b border-[#252a38] p-2">
              <div className="relative">
                <Search
                  size={12}
                  className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-[#475569]"
                />
                <input
                  ref={inputRef}
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Tìm kiếm..."
                  className="w-full rounded-md border border-[#252a38] bg-[#0d0f14] py-1.5 pl-7 pr-3 text-[12px] text-[#e2e8f0] outline-none transition-colors placeholder:text-[#475569] focus:border-[#3b82f6]"
                />
              </div>
            </div>
          )}

          <div className="overflow-y-auto py-1" style={{ maxHeight }}>
            {filtered.length === 0 ? (
              <div className="px-3 py-4 text-center text-[12px] text-[#64748b]">
                Không có kết quả
              </div>
            ) : (
              filtered.map((opt) => {
                const isSelected = opt.value === value;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => handleSelect(opt.value)}
                    className={`flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-[12px] font-medium transition-colors ${
                      isSelected
                        ? 'bg-[#1e2a4a] text-[#7dd3fc]'
                        : 'text-[#e2e8f0] hover:bg-[#1c2030]'
                    }`}
                    role="option"
                    aria-selected={isSelected}
                  >
                    <span className="truncate">{opt.label}</span>
                    {isSelected && <Check size={12} className="shrink-0 text-[#3b82f6]" />}
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};